import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, statusConfig, priorityConfig, nowChile } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  User, 
  Clock,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Sortable Ticket Card Component - whole card is draggable
const SortableTicketCard = ({ ticket, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ticket.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isSLABreached = ticket.sla_resolution_deadline && new Date(ticket.sla_resolution_deadline) < nowChile();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="p-3 rounded-lg bg-white border border-gray-200 hover:border-[#7FD02E] transition-colors cursor-grab active:cursor-grabbing select-none shadow-sm hover:shadow-md touch-none"
      onClick={onClick}
      data-testid={`kanban-ticket-${ticket.id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-xs text-[#7FD02E]">{ticket.ticket_number}</span>
        {isSLABreached && (
          <AlertTriangle className="w-3 h-3 text-rose-500" />
        )}
      </div>
      <p className="font-medium text-sm text-gray-800 break-words">{ticket.title}</p>
      <div className="flex items-center gap-2 mt-2">
        <Badge className={`${priorityConfig[ticket.priority]?.color} text-xs py-0`}>
          {priorityConfig[ticket.priority]?.label}
        </Badge>
        {ticket.assignee && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-[#7FD02E]/20 flex items-center justify-center">
              <User className="w-2 h-2 text-[#7FD02E]" />
            </div>
            <span className="text-xs text-gray-500">{ticket.assignee.first_name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Kanban Column Component - now a droppable zone for empty/cross-column drops
const KanbanColumn = ({ status, tickets, children }) => {
  const config = statusConfig[status];
  const { setNodeRef, isOver } = useDroppable({ id: status });
  
  return (
    <div className="flex-1 min-w-0" data-testid={`kanban-column-${status}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge className={config?.color}>{config?.label}</Badge>
          <span className="text-sm text-gray-500">({tickets.length})</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`space-y-2 min-h-[200px] p-2 rounded-lg transition-colors ${
          isOver ? 'bg-[#7FD02E]/10 ring-2 ring-[#7FD02E]/40' : 'bg-gray-100'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default function KanbanPage() {
  const { user, isJefatura } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [solucionadores, setSolucionadores] = useState([]);
  const [filterAssignee, setFilterAssignee] = useState('');
  const [activeTicket, setActiveTicket] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Define columns - excluding 'cerrado' for active kanban
  const columns = ['nuevo', 'asignado', 'en_progreso', 'en_espera', 'resuelto', 'validacion'];

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page_size', '100');
      if (filterAssignee) params.append('assignee_id', filterAssignee);
      
      const response = await axios.get(`${API_URL}/tickets?${params.toString()}`);
      // Filter out closed tickets for kanban view
      const activeTickets = response.data.tickets.filter(t => t.status !== 'cerrado');
      setTickets(activeTickets);
    } catch (error) {
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, [filterAssignee]);

  const fetchSolucionadores = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users/solucionadores`);
      setSolucionadores(response.data);
    } catch (error) {
      console.error('Error fetching solucionadores:', error);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    if (isJefatura) {
      fetchSolucionadores();
    }
  }, [filterAssignee, isJefatura, fetchTickets, fetchSolucionadores]);

  const getTicketsByStatus = (status) => {
    return tickets.filter(t => t.status === status);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const ticket = tickets.find(t => t.id === active.id);
    setActiveTicket(ticket);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTicket(null);

    if (!over) return;

    // Find the column where we dropped (by checking if over.id is a status)
    let newStatus = null;
    if (columns.includes(over.id)) {
      newStatus = over.id;
    } else {
      // Find the ticket we dropped over and get its status
      const overTicket = tickets.find(t => t.id === over.id);
      if (overTicket) {
        newStatus = overTicket.status;
      }
    }

    if (!newStatus) return;

    const activeTicket = tickets.find(t => t.id === active.id);
    if (!activeTicket || activeTicket.status === newStatus) return;

    // Optimistic update
    setTickets(prev => 
      prev.map(t => t.id === active.id ? { ...t, status: newStatus } : t)
    );

    try {
      await axios.patch(`${API_URL}/tickets/${active.id}/status`, {
        status: newStatus
      });
      toast.success(`Estado actualizado a ${statusConfig[newStatus]?.label}`);
    } catch (error) {
      // Revert on error
      fetchTickets();
      toast.error('Error al actualizar estado');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-gray-500">Cargando tablero...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="kanban-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">Tablero Kanban</h1>
          <p className="text-gray-500">
            Arrastra los tickets para cambiar su estado
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isJefatura && (
            <Select value={filterAssignee || "all"} onValueChange={(value) => setFilterAssignee(value === "all" ? "" : value)}>
              <SelectTrigger className="w-48" data-testid="filter-assignee-kanban">
                <SelectValue placeholder="Todos los solucionadores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {solucionadores.map((sol) => (
                  <SelectItem key={sol.id} value={sol.id}>
                    {sol.first_name} {sol.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={fetchTickets} className="border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="refresh-kanban-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="pb-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-3 w-full">
            {columns.map((status) => {
              const columnTickets = getTicketsByStatus(status);
              return (
                <KanbanColumn key={status} status={status} tickets={columnTickets}>
                  <SortableContext
                    items={columnTickets.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {columnTickets.map((ticket) => (
                      <SortableTicketCard
                        key={ticket.id}
                        ticket={ticket}
                        onClick={() => navigate(`/tickets/${ticket.id}`)}
                      />
                    ))}
                  </SortableContext>
                  {columnTickets.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      Sin tickets
                    </div>
                  )}
                </KanbanColumn>
              );
            })}
          </div>

          <DragOverlay>
            {activeTicket ? (
              <div className="p-3 rounded-lg bg-white border border-[#7FD02E] shadow-lg shadow-[#7FD02E]/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-[#7FD02E]">{activeTicket.ticket_number}</span>
                </div>
                <p className="font-medium text-sm text-gray-800">{activeTicket.title}</p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
